import React from 'react';
import { Crown, ShieldCheck, Network, UserCog, ChevronDown, ChevronRight, Mail } from 'lucide-react';

interface OrgNode {
  id: string;
  display_name: string;
  email?: string;
  role: string;
  department: string;
  position: string;
  manager_id: string | null;
  team_leader_of: string;
  children: OrgNode[];
}

interface DepartmentConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
}

type Tier = 'executive' | 'admin' | 'lead' | 'employee';

interface EmployeeCardProps {
  node: OrgNode;
  tier: Tier;
  hasChildren: boolean;
  isCollapsed: boolean;
  isMatch: boolean;
  deptConfig: DepartmentConfig;
  onToggle: (id: string) => void;
  onReassign?: (node: OrgNode) => void;
  isAdmin?: boolean;
  isRoot?: boolean;
}

function getTierStyles(tier: Tier) {
  switch (tier) {
    case 'executive':
      return {
        borderColor: 'border-violet-400 dark:border-violet-600',
        shadow: 'shadow-md hover:shadow-lg',
        headerBg: 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white',
        headerIcon: <Crown className="h-3 w-3 text-amber-300" />,
        headerLabel: 'Executive Leadership',
        avatarBg: 'bg-gradient-to-br from-violet-600 to-indigo-700 ring-violet-300',
        avatarIcon: <Crown className="h-6 w-6 text-amber-300" />,
      };
    case 'admin':
      return {
        borderColor: 'border-indigo-400 dark:border-indigo-600',
        shadow: 'shadow-sm hover:shadow-md',
        headerBg: 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white',
        headerIcon: <ShieldCheck className="h-3 w-3 text-blue-200" />,
        headerLabel: 'Department Administrator',
        avatarBg: 'bg-gradient-to-br from-indigo-600 to-blue-600 ring-indigo-300',
      };
    case 'lead':
      return {
        borderColor: 'border-teal-400 dark:border-teal-600',
        shadow: 'shadow-sm hover:shadow-md',
        headerBg: 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white',
        headerIcon: <Network className="h-3 w-3 text-teal-200" />,
        headerLabel: 'Team Lead / Manager',
        avatarBg: 'bg-gradient-to-br from-teal-600 to-emerald-600 ring-teal-300',
      };
    case 'employee':
    default:
      return {
        borderColor: 'border-[#E8E8F5] hover:border-[#D1D1E0]',
        shadow: 'shadow-sm hover:shadow',
        headerBg: 'bg-[#F2F2FA] text-[#747C9C]',
        headerIcon: null,
        headerLabel: 'Functional Role',
        avatarBg: 'bg-gradient-to-br from-slate-600 to-slate-800 ring-slate-200',
      };
  }
}

export function EmployeeCard({
  node,
  tier,
  hasChildren,
  isCollapsed,
  isMatch,
  deptConfig,
  onToggle,
  onReassign,
  isAdmin = false,
  isRoot = false,
}: EmployeeCardProps) {
  const styles = getTierStyles(tier);
  const isSuperAdmin = tier === 'executive';
  const isDeptAdmin = tier === 'admin';
  const isLead = tier === 'lead';

  return (
    <div className="flex flex-col items-center">
      {/* Node Card */}
      <div
        className={`employee-card group relative rounded-xl border bg-white transition-all duration-200 ${
          isMatch
            ? 'ring-2 ring-[#6825F5] ring-offset-2 border-[#6825F5] shadow-lg'
            : styles.borderColor + ' ' + styles.shadow
        }`}
        style={{ width: '220px' }}
      >
        {/* Tier Header Strip */}
        <div
          className={`flex items-center justify-between rounded-t-xl px-3 py-1 text-[10px] font-semibold tracking-wide ${styles.headerBg}`}
        >
          <div className="flex items-center gap-1.5 truncate">
            {styles.headerIcon}
            <span>{styles.headerLabel}</span>
          </div>

          {/* Direct reports count pill */}
          {hasChildren && (
            <span
              className={`rounded-full px-1.5 py-0.2 text-[9px] font-bold ${
                isSuperAdmin || isDeptAdmin || isLead ? 'bg-white/20 text-white' : 'bg-white text-[#11183F]'
              }`}
            >
              {node.children.length}
            </span>
          )}
        </div>

        {/* Card Body */}
        <div className="p-3.5">
          {/* Avatar & Department Icon Badge */}
          <div className="relative mb-2 flex items-center justify-center">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full text-base font-bold text-white shadow-sm ring-2 ${styles.avatarBg}`}
            >
              {node.display_name
                .split(' ')
                .map((w) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </div>

            {/* Department icon badge attached to avatar */}
            <div
              className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-white shadow-xs"
              style={{ color: deptConfig.color }}
              title={deptConfig.label}
            >
              {deptConfig.icon}
            </div>
          </div>

          {/* Name */}
          <h3 className="text-center text-sm font-bold text-[#11183F] leading-tight truncate" title={node.display_name}>
            {node.display_name}
          </h3>

          {/* Position or Role */}
          <p className="mt-0.5 text-center text-[11px] text-[#747C9C] truncate" title={node.position}>
            {isSuperAdmin ? 'Super Admin' : node.position || node.role}
          </p>

          {/* Email badge for superadmin */}
          {(isSuperAdmin || node.email) && (
            <p className="text-center text-[10px] text-[#747C9C]/80 truncate font-mono mt-0.5" title={node.email || 'uzef@durranitech.com'}>
              {node.email || (isSuperAdmin ? 'uzef@durranitech.com' : '')}
            </p>
          )}

          {/* Department Badge */}
          <div className="mt-2 flex justify-center">
            <span
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-medium ${deptConfig.bgColor} ${deptConfig.borderColor}`}
            >
              {deptConfig.icon}
              <span>{deptConfig.label}</span>
            </span>
          </div>

          {/* Card Footer: Expand/Collapse & Reassign Manager Actions */}
          <div className="mt-2.5 flex items-center justify-center gap-1.5 border-t border-[#E8E8F5]/40 pt-2">
            {hasChildren && (
              <button
                type="button"
                onClick={() => onToggle(node.id)}
                className="flex items-center gap-1 rounded-full border border-[#E8E8F5] bg-white px-2 py-0.5 text-[10px] font-medium text-[#747C9C] hover:bg-[#F2F2FA]"
                aria-label={isCollapsed ? 'Expand Reports' : 'Collapse Reports'}
                title={isCollapsed ? 'Show Direct Reports' : 'Hide Direct Reports'}
              >
                {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                <span>
                  {node.children.length} {node.children.length === 1 ? 'Report' : 'Reports'}
                </span>
              </button>
            )}

            {isAdmin && onReassign && (
              <button
                type="button"
                onClick={() => onReassign(node)}
                className="flex h-6 w-6 items-center justify-center rounded-full border border-[#E8E8F5] bg-white text-[#747C9C] hover:bg-[#F2EDFF] hover:text-[#6825F5] hover:border-[#6825F5]/40 transition-colors"
                aria-label="Change manager"
                title="Change Reporting Manager"
              >
                <UserCog className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}